import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import WhatsAppFloat from "@/components/site/WhatsAppFloat";
import BoardTransferForm from "@/components/site/BoardTransferForm";
import { BoardTransferIntro } from "@/components/site/BoardTransferSection";
import { IMAGES, waLink, WA_CARGO_MSG } from "@/lib/site";
import { Plane, Truck, Ship, PackageCheck } from "lucide-react";

const STEPS = [
  {
    icon: Plane,
    title: "1 · Fly light to Medan or Padang",
    desc: "Skip the airline board fees on the KNO/PDG to GNS leg — travel with hand luggage only.",
  },
  {
    icon: Truck,
    title: "2 · We collect your board bags",
    desc: "Our cargo partners pick up your quiver at the airport or transit hub in Medan (KNO) or Padang (PDG).",
  },
  {
    icon: Ship,
    title: "3 · Overland & ferry forwarding",
    desc: "Boards travel securely overland and by ferry toward South Nias — single boards or multi-board coffins.",
  },
  {
    icon: PackageCheck,
    title: "4 · Waiting at Raffiel Nias reception",
    desc: "Your boards are stored safely at our beachfront hotel, ready before your first session at Lagundri Bay.",
  },
];

export default function BoardTransfer() {
  return (
    <div className="bg-sand-50 text-ink font-sans antialiased min-h-screen">
      <Navbar />
      <main>
        <section className="relative min-h-[55vh] flex flex-col justify-end text-white overflow-hidden" data-testid="board-transfer-hero">
          <img
            src={IMAGES.transferHero}
            alt="Raffiel Nias airport transfer vehicle carrying surfboard bags to Sorake Beach"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(14,41,40,0.9) 0%, rgba(14,41,40,0.45) 60%, rgba(14,41,40,0.1) 100%)",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-6 pb-14 pt-32 w-full z-10 anim-rise">
            <span className="text-xs font-mono-clean text-terracotta uppercase tracking-widest font-semibold">
              Board Bag Shipment · Medan & Padang → Nias
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.05] text-white mt-3 max-w-3xl">
              Your boards, delivered. Airline fees, avoided.
            </h1>
            <p className="text-base sm:text-lg text-sand-50/90 leading-relaxed font-light mt-4 max-w-2xl">
              Dedicated surfboard cargo transfer from Medan (KNO) and Padang
              (PDG) straight to Raffiel Nias reception at Sorake Beach.
            </p>
          </div>
        </section>

        <section className="py-20 bg-sand-50" data-testid="board-transfer-steps">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-12">
              <span className="text-xs font-mono-clean text-terracotta uppercase tracking-wider font-semibold">
                The Process
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-ocean-900 mt-2">
                How board transfer works
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {STEPS.map((s) => (
                <div key={s.title} className="bg-white p-6 rounded-2xl border border-sand-200 shadow-sm">
                  <div className="w-10 h-10 rounded-xl bg-ocean-700/10 flex items-center justify-center mb-4">
                    <s.icon size={20} className="text-ocean-700" />
                  </div>
                  <h3 className="font-display font-bold text-ocean-900 text-base">{s.title}</h3>
                  <p className="text-xs text-fog mt-2 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-ocean-900 text-white" data-testid="board-transfer-form-section">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <BoardTransferIntro />
              <div className="lg:col-span-7">
                <BoardTransferForm />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-sand-100 border-t border-sand-200" data-testid="board-transfer-note">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-ocean-900">
              Fixed route pricing — no surprises
            </h2>
            <p className="text-sm text-fog mt-3 max-w-xl mx-auto">
              Medan (KNO) → Raffiel Nias: <strong className="text-ocean-900">IDR 1,300,000</strong> per board bag.
              Padang (PDG) → Raffiel Nias: <strong className="text-ocean-900">IDR 1,600,000</strong> per board bag.
              Send the enquiry form above or message us to lock in your pickup window.
            </p>
            <a
              href={waLink(WA_CARGO_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="board-transfer-bottom-whatsapp"
              className="mt-6 inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-ocean-700 text-white text-xs font-semibold uppercase tracking-wider hover:bg-ocean-800 transition-colors"
            >
              Arrange Pickup on WhatsApp
            </a>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
